'use client'

import { useEffect, useState } from 'react'
import { debounce } from 'throttle-debounce'

import { useKeenSlider } from 'keen-slider/react'
import { KeenSliderArrows } from '@/components/keen-slider/KeenSliderPlugins'
import 'keen-slider/keen-slider.min.css'
import GridArchiveItem, { GridArchiveItemProps } from '@/components/grid-archive/GridArchiveItem'

/**
 * --------------------------------------------------------------------------
 * LatestPostsGrid
 * --------------------------------------------------------------------------
 **/
export function LatestPostsGrid({
  posts,
  fullWidth,
}: {
  posts: GridArchiveItemProps[]
  fullWidth?: boolean
}) {
  const [isDisabled, setIsDisabled] = useState(false)
  const [isPartial, setIsPartial] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1.25,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 500px)': {
        slides: {
          perView: 2.25,
          spacing: 24,
        },
      },
      '(min-width: 1200px)': {
        disabled: true,
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)

      // Debounce to avoid multiple rapid calls
      debounce(250, () => {
        const isCurrentPartial = !s.options.disabled && !Number.isInteger(s.options.slides?.perView)

        setIsPartial(isCurrentPartial)
      })()
    },
  })

  if (!Array.isArray(posts) || !posts?.length || posts?.length === 0) {
    return null
  }

  const gridClass = `
    [@media(min-width:1200px)]:grid
    [@media(min-width:1200px)]:grid-cols-12

    [@media(min-width:1200px)]:gap-m
  `

  return (
    <div className={`${isPartial ? 'mr-[calc(var(--container-gutter)*-1)]' : ''}`}>
      <div ref={sliderRef} className={`keen-slider ${gridClass}`}>
        {posts.map((post, index) => {
          const postId = post?.post_id

          if (!postId) {
            return null
          }

          let itemClass = ''

          if (fullWidth) {
            itemClass = index < 3 ? `col-span-4` : `col-span-3`
          } else {
            itemClass = index < 3 ? `col-span-4` : `col-span-3 xl:col-span-4`
          }

          return (
            <div className={`keen-slider__slide ${itemClass}`} key={postId}>
              <GridArchiveItem hit={post as any} size={index < 2 ? 'l' : null} />
            </div>
          )
        })}
      </div>

      <div className={`${isPartial ? 'px-[var(--container-gutter)]' : ''}`}>
        <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
      </div>
    </div>
  )
}

/**
 * --------------------------------------------------------------------------
 * LatestPostsSlider
 * --------------------------------------------------------------------------
 **/
export function LatestPostsSlider({
  posts,
  fullWidth = true,
}: {
  posts: GridArchiveItemProps[]
  fullWidth?: boolean
}) {
  const [isDisabled, setIsDisabled] = useState(false)
  const [isPartial, setIsPartial] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1.25,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 500px)': {
        slides: {
          perView: 2.25,
          spacing: 16,
        },
      },
      '(min-width: 989px)': {
        slides: {
          perView: fullWidth ? 3 : 2,
          spacing: 16,
        },
      },
      '(min-width: 1199px)': {
        slides: {
          perView: fullWidth ? 4 : 3,
          spacing: 16,
        },
      },
      '(min-width: 1519px)': {
        slides: {
          perView: fullWidth ? 5 : 4,
          spacing: 24,
        },
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)

      // Debounce to avoid multiple rapid calls
      debounce(250, () => {
        const isCurrentPartial = !s.options.disabled && !Number.isInteger(s.options.slides?.perView)

        setIsPartial(isCurrentPartial)
      })()
    },
  })

  if (!posts || posts.length === 0) {
    return null
  }

  return (
    <div className={`${isPartial ? 'mr-[calc(var(--container-gutter)*-1)]' : ''}`}>
      <div ref={sliderRef} className={`keen-slider`}>
        {posts.map((post) => {
          const postId = post?.post_id

          if (!postId) {
            return null
          }

          return (
            <div className="keen-slider__slide" key={postId}>
              <GridArchiveItem hit={post} />
            </div>
          )
        })}
      </div>

      <div className={`${isPartial ? 'px-[var(--container-gutter)]' : ''}`}>
        <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
      </div>
    </div>
  )
}
